import { InputSelectProps } from './InputSelect';
import useGroupsContext from '@contexts/GroupsContext/hook';
import useFeaturePermissionsContext from '@contexts/FeaturePermissionsContext/hook';
import { PermissionFilter, permiteProduto } from '@utils/permissionFilter';
import {
  filtrarComunidadesPorPapel,
  paraComunidadesSelecionaveis,
} from '@utils/comunidadesSelecionaveis';
import { Controller } from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import {
  Chip,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
} from '@mui/material';
import { UserCommunity, UserProduct, UserRole } from '@interfaces/auth';
import { twMerge } from 'tailwind-merge';
import { styles } from './styles';
import useToastContext from '@contexts/ToastContext/hook';
import { useFirstRender } from '@hooks/useFirstRender';
import {
  getSelectedFilters,
  setSelectedFilters,
} from '@repository/selectedFilters';
import { resolveDefaultCommunityIds } from '@utils/communitiesStatusFilter';
import { CommunitiesSelectionModal } from './CommunitiesSelectionModal';

type Props = Omit<InputSelectProps, 'options' | 'type'> & {
  /** Recorte por PAPEL, para os seletores em que o papel É o assunto (§6.3.2). */
  userRolesFilter?: UserRole[];
  /**
   * Recorte por PERMISSÃO (§6.3). Quando vem, decide sozinho, e a lista parte de todas as
   * comunidades alcançadas — inclusive as de colaboração, que nenhum papel de comissão cobre.
   */
  permissionFilter?: PermissionFilter;
  /**
   * Um recorte EXTRA, por lista de ids, aplicado depois do recorte por permissão.
   *
   * Existe porque o modo "Tudo o que eu alcanço" do painel tem um critério que este componente não
   * sabe expressar: além de `dashboard:view`, a comunidade precisa ter escopo de relatório que alguma
   * família de blocos admita. Sem ele o seletor afirmava incluir uma comunidade que todo agregado
   * depois omitia.
   *
   * Lista pronta, e não um segundo predicado: quem tem o critério é o painel, e passar a resposta
   * evita este componente aprender vocabulário de escopo — que ele não usa para mais nada.
   */
  idsElegiveis?: number[];
  selectedFiltersPage: string;
  onChangeCallback?: (ids: number[]) => void;
  /**
   * Communities a drill-down link handed over, which win over the stored selection.
   *
   * Without them the detail falls back to whatever the browser remembers, or to every eligible community
   * when it remembers nothing - so a link copied, bookmarked or opened elsewhere explains a different
   * cohort than the card that was clicked, and says nothing about having done so.
   */
  initialBotConfigIds?: number[];
};

export const InputCommunities = (props: Props) => {
  const {
    name,
    control,
    onChangeCallback,
    selectedFiltersPage,
    userRolesFilter,
    permissionFilter,
    idsElegiveis,
    initialBotConfigIds,
    ...restProps
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const { communities: userCommunities } = useGroupsContext();

  const { getUserPermissions } = useFeaturePermissionsContext();

  const porPermissaoOuPapel = permissionFilter
    ? userCommunities.filter((community) =>
        permiteProduto(getUserPermissions, permissionFilter, community.id),
      )
    : filtrarComunidadesPorPapel(userCommunities, userRolesFilter ?? []);

  const allCommunities = idsElegiveis
    ? porPermissaoOuPapel.filter((community) =>
        idsElegiveis.includes(Number(community.id)),
      )
    : porPermissaoOuPapel;

  /**
   * Narrowed to what this user actually holds under the current role. A link carries ids from whoever
   * built it, and selecting a community the viewer has no claim on would send it to the API on their
   * behalf; an id that survives nothing falls back to the stored selection rather than to an empty screen.
   */
  const handedOverBotConfigIds = initialBotConfigIds?.filter((id) =>
    allCommunities.some((community) => community.id === id),
  );
  const hasHandedOver = !!handedOverBotConfigIds?.length;

  /**
   * As ativas, e não todas: uma comunidade encerrada meses atrás continua no sistema pelas métricas, e
   * até aqui ela entrava calada em cada número da tela.
   */
  const defaultBotConfigIds = resolveDefaultCommunityIds(allCommunities);

  const selectedFilters = getSelectedFilters(selectedFiltersPage);

  /**
   * A seleção lembrada é PODADA para uso, e a poda não é gravada.
   *
   * Duas regras, e as duas vêm de findings desta série:
   *
   * 1. Ninguém usa id que a lista não mostra. Os ids de um link já eram filtrados — "selecionar uma
   *    comunidade sobre a qual o visitante não tem direito a mandaria à API em nome dele" —, e o
   *    guardado no navegador não era: entre visitas um grant é revogado, o `report_scope` muda, a
   *    comunidade é encerrada.
   *
   * 2. **Mas a poda é de quem LÊ, não do que está guardado.** Este componente serve sete telas com
   *    elegibilidades diferentes; gravar a poda fazia a tela mais estreita apagar a seleção das
   *    outras. Abrir um detalhe pelo menu — que cai num papel de partida — destruía a seleção do modo
   *    "Tudo o que eu alcanço" que a pessoa tinha feito no painel, e a volta mostrava outra fatia
   *    financeira sem dizer nada.
   *
   * É a mesma regra do papel lembrado: guarda-se decisão, não dedução. Nada de "stale para sempre" —
   * quem lê poda na leitura, sempre.
   */
  const lembradas = (selectedFilters.botConfigIds ??
    []) as UserCommunity['id'][];

  const lembradasElegiveis = lembradas.filter((id) =>
    allCommunities.some((community) => community.id === id),
  );

  /*
    **O que se guarda: o que veio de FORA e o que a pessoa MUDOU. Nunca o que a tela deduziu.**

    É a mesma regra que o `ConversionFilters` aplica ao papel, e escrever as duas iguais é o ponto:
    três rodadas de revisão desta série foram, cada uma, meia regra aplicada a um campo.

      - de fora (ids de um link)                -> grava, é intenção de navegação;
      - mudança da pessoa                       -> grava (pelo `onChange`, abaixo);
      - default de quem nunca escolheu nada     -> grava, é a primeira visita;
      - poda de elegibilidade desta tela        -> NÃO grava.
  */
  if (hasHandedOver)
    setSelectedFilters(selectedFiltersPage, {
      botConfigIds: handedOverBotConfigIds,
    });
  else if (!lembradas.length)
    setSelectedFilters(selectedFiltersPage, {
      botConfigIds: defaultBotConfigIds,
    });

  const initialSelectedIds: UserCommunity['id'][] = hasHandedOver
    ? (handedOverBotConfigIds as UserCommunity['id'][])
    : lembradasElegiveis.length
    ? lembradasElegiveis
    : defaultBotConfigIds;

  const handleClose = () => {
    setIsOpen(false);
  };

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={initialSelectedIds}
      render={({ field }) => {
        const toast = useToastContext();
        const triggerNoneSelectedToast = useCallback(
          (ids: UserProduct['id'][]) => {
            !ids.length &&
              toast.setContent(
                'Você deve selecionar pelo menos uma comunidade!',
                'warning',
              );
          },
          [],
        );

        useEffect(() => {
          triggerNoneSelectedToast(field.value);
          field.onChange(field.value);
        }, []);

        const selectedCommunities = allCommunities.filter((product) =>
          field.value.includes(product.id),
        );

        const onSubmit = (ids: UserProduct['id'][]) => {
          triggerNoneSelectedToast(ids);
          field.onChange(ids);
          onChangeCallback?.(ids);
          setSelectedFilters(selectedFiltersPage, { botConfigIds: ids });
          handleClose();
        };

        const isFirstRender = useFirstRender();
        useEffect(() => {
          if (isFirstRender) return;

          onSubmit(defaultBotConfigIds);
        }, [JSON.stringify(userRolesFilter)]);

        return (
          <>
            <FormControl
              variant="outlined"
              className={twMerge(styles, restProps.className)}
              error={restProps.error}
            >
              <InputLabel htmlFor="products">Comunidades</InputLabel>
              <Select
                name="communities"
                label="Comunidades"
                // O fakeValue é usado para forçar o label do Mui ao "estado ativo" (para a borda superior do input)
                value="fakeValue"
                displayEmpty
                open={false}
                MenuProps={{ disableScrollLock: true }}
                onClick={() => setIsOpen(true)}
                renderValue={() => {
                  return (
                    <div className="absolute flex items-center -translate-y-1/2 top-1/2 pr-sm max-w-[calc(100%-4.5rem)]">
                      {!!selectedCommunities.length && (
                        <Chip
                          key={selectedCommunities[0]?.id}
                          label={selectedCommunities[0]?.name}
                        />
                      )}
                      {selectedCommunities.length > 1 && (
                        <span className="text-sm ml-1">
                          +{selectedCommunities.length - 1}
                        </span>
                      )}
                    </div>
                  );
                }}
              >
                <option value="fakeValue" />
              </Select>
              <FormHelperText>{restProps.helperText}</FormHelperText>
            </FormControl>

            <CommunitiesSelectionModal
              isOpen={isOpen}
              onClose={handleClose}
              communities={paraComunidadesSelecionaveis(allCommunities)}
              selectedIds={field.value}
              onSubmit={onSubmit}
            />
          </>
        );
      }}
    />
  );
};
